<template>
  <v-row v-if="auditPeriod && options">
    <template v-if="!mayStartEdit">
      <v-col cols="12" class="mt-12">
        <v-alert type="warning">
          {{ $t("user.auditPeriod.startEdit.notAllowed") }}
        </v-alert>
      </v-col>
    </template>

    <template v-if="startedPrechecks.length > 0">
      <v-col cols="12" class="mt-12">
        <h2 class="text-h5">{{ $t("user.auditPeriod.prechecks") }}</h2>
      </v-col>

      <v-col v-for="fund in startedPrechecks" :key="fund.id" cols="6">
        <FundCard :fund="fund" :audit-period="auditPeriod" :may-start-edit="mayStartEdit" />
      </v-col>
    </template>

    <template v-if="startedFunds.length > 0">
      <v-col cols="12" class="mt-12">
        <h2 class="text-h5">{{ $t("user.auditPeriod.funds") }}</h2>
      </v-col>

      <v-col v-for="fund in startedFunds" :key="fund.id" cols="6">
        <FundCard :fund="fund" :audit-period="auditPeriod" :may-start-edit="mayStartEdit" />
      </v-col>
    </template>

    <template v-if="newPrechecks.length > 0">
      <v-col cols="12" class="mt-12">
        <h2 class="text-h5">{{ $t("user.auditPeriod.newPrechecks") }}</h2>
      </v-col>

      <v-col v-for="fund in newPrechecks" :key="fund.id" cols="12" lg="6">
        <FundCard :fund="fund" :audit-period="auditPeriod" :may-start-edit="mayStartEdit" />
      </v-col>
    </template>

    <template v-if="newFunds.length > 0">
      <v-col cols="12" class="mt-12">
        <h2 class="text-h5">{{ $t("user.auditPeriod.newFunds") }}</h2>
      </v-col>

      <v-col v-for="fund in newFunds" :key="fund.id" cols="12" lg="6">
        <FundCard :fund="fund" :audit-period="auditPeriod" :may-start-edit="mayStartEdit" />
      </v-col>
    </template>

    <v-col cols="12" class="mt-12"></v-col>

    <v-col cols="12">
      <AuditPeriodFiles :audit-period="auditPeriod" />
    </v-col>

    <v-col cols="12" v-if="auditPeriod.attachmentEnabled">
      <AuditPeriodFilesForm :audit-period="auditPeriod" type="user" />
    </v-col>
  </v-row>
</template>

<script>
import { filter, sortBy, prop } from "ramda";
import FundCard from "@/components/User/FundCard";
import AuditPeriodFiles from "@/components/AuditPeriod/AuditPeriodFiles";
import AuditPeriodFilesForm from "@/components/AuditPeriod/AuditPeriodFilesForm";
import OptionsQuery from "@/gql/queries/auditor/OptionsQuery.gql";

export default {
  components: {
    FundCard,
    AuditPeriodFiles,
    AuditPeriodFilesForm,
  },

  props: {
    auditPeriod: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      options: null,
    };
  },

  apollo: {
    options: {
      query: OptionsQuery,
      fetchPolicy: "cache-and-network",
    },
  },

  computed: {
    startedFunds() {
      return sortBy(
        prop("createdAt"),
        filter((fund) => fund.state !== "NEW" && !fund.precheck, this.auditPeriod.funds)
      );
    },

    startedPrechecks() {
      return sortBy(
        prop("createdAt"),
        filter((fund) => fund.state !== "NEW" && fund.precheck, this.auditPeriod.funds)
      );
    },

    newFunds() {
      return sortBy(
        prop("createdAt"),
        filter((fund) => fund.state === "NEW" && !fund.precheck, this.auditPeriod.funds)
      );
    },

    newPrechecks() {
      return sortBy(
        prop("createdAt"),
        filter((fund) => fund.state === "NEW" && fund.precheck, this.auditPeriod.funds)
      );
    },

    mayStartEdit() {
      const startEdit = this.options?.find((option) => option.key === "startEdit")?.value;

      // If startEdit is not set, the user cannot start the edit
      if (!startEdit) {
        return false;
      }

      const startEditDate = new Date(startEdit);
      const now = new Date();

      // If the startEdit date is in the previous year, the user cannot start the edit
      if (startEditDate.getFullYear() < this.auditPeriod.year) {
        return false;
      }

      return now > startEditDate;
    },
  },
};
</script>
